import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { NotificationSeverity } from '@/lib/notifications';

type Props = {
  title: string;
  body: string | null;
  unread: boolean;
  /** Alert severity; null = a plain notification (no severity badge). */
  severity?: NotificationSeverity | null;
  /** The single email account this notification is about, or null/undefined. */
  emailAccountId?: number | null;
  /**
   * `compact` (bell dropdown): inline unread dot, bold title only when unread,
   * truncated lines. `feed` (full page): no inline dot (the row renders its
   * own), always-bold larger title, wrapping body.
   */
  variant?: 'compact' | 'feed';
  /** Collapsed duplicates (`collapseRepeats`); > 1 renders a `×N` chip. */
  repeatCount?: number;
  className?: string;
};

// Finds the email address baked into a notification's plain-text title/body.
const EMAIL_PATTERN = /[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+/;

// Bolds the matched email regardless of emailAccountId; only links it when
// emailAccountId is non-null. Plain text when no email substring is found.
function withEmailLink(text: string, emailAccountId: number | null | undefined): ReactNode {
  const match = EMAIL_PATTERN.exec(text);
  if (!match) return text;
  const start = match.index;
  const end = start + match[0].length;
  return (
    <>
      {text.slice(0, start)}
      <EmailAccountLink email={match[0]} emailAccountId={emailAccountId ?? null} />
      {text.slice(end)}
    </>
  );
}

// <span>, not <Link>/<a> — this can sit inside the bell dropdown's <button>
// row, which can't contain a focusable descendant. stopPropagation stops a
// click here from also triggering that button's mark-as-read.
function EmailAccountLink({
  email,
  emailAccountId,
}: {
  email: string;
  emailAccountId: number | null;
}) {
  const navigate = useNavigate();
  return (
    <span
      onClick={
        emailAccountId == null
          ? undefined
          : (e) => {
              e.stopPropagation();
              void navigate({ to: '/email-accounts/$id', params: { id: emailAccountId } });
            }
      }
      className={cn(
        'font-semibold text-foreground',
        emailAccountId != null && 'cursor-pointer hover:underline',
      )}
    >
      {email}
    </span>
  );
}

// Kept unexported so this stays a component-only module (Fast-Refresh rule).
const SEVERITY_BADGE_VARIANT: Record<NotificationSeverity, BadgeProps['variant']> = {
  [NotificationSeverity.CRITICAL]: 'critical',
  [NotificationSeverity.WARNING]: 'warning',
};

const SEVERITY_LABEL: Record<NotificationSeverity, string> = {
  [NotificationSeverity.CRITICAL]: 'Critical',
  [NotificationSeverity.WARNING]: 'Warning',
};

/**
 * The title + body + indicator block for a single notification. Shared by the
 * bell dropdown row and the full-page feed row so the unread styling and the
 * severity accent live in one place rather than being copy-pasted between the
 * two surfaces.
 *
 * Two independent signals, so a row's state is never ambiguous:
 * - **unread** → leading brand dot + bold title. Fires for any unread row,
 *   alert or plain, and disappears the moment it's read.
 * - **alert** (severity !== null) → a coloured severity `Badge` after the
 *   title. Fires for any alert regardless of read state, so a *read* alert
 *   stays visually distinct from an *unread* plain notification — previously
 *   both only differed by a subtle dot colour.
 */
export function NotificationTitleBody({
  title,
  body,
  unread,
  severity = null,
  emailAccountId = null,
  variant = 'compact',
  repeatCount = 1,
  className,
}: Props) {
  const feed = variant === 'feed';
  const chip = feed
    ? 'shrink-0 rounded px-1.5 py-0 text-[11px] font-semibold leading-[18px]'
    : 'shrink-0 rounded px-1.5 py-0 text-[10px] font-semibold leading-4';
  return (
    <div className={cn('min-w-0', className)}>
      <div className={cn('flex min-w-0 items-center', feed ? 'gap-2' : 'gap-1.5')}>
        {unread && !feed && (
          <span
            aria-hidden
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand"
          />
        )}
        <span
          className={cn(
            'min-w-0 truncate text-foreground',
            feed ? 'text-[13px] font-bold leading-5' : 'text-[13px]',
            unread && !feed && 'font-semibold',
          )}
        >
          {withEmailLink(title, emailAccountId)}
        </span>
        {severity !== null && (
          <Badge variant={SEVERITY_BADGE_VARIANT[severity]} className={chip}>
            {SEVERITY_LABEL[severity]}
          </Badge>
        )}
        {repeatCount > 1 && (
          <Badge
            variant="neutral"
            className={chip}
            title={`Repeated ${repeatCount} times`}
          >
            ×{repeatCount}
          </Badge>
        )}
      </div>
      {body && (
        <p
          className={cn(
            'text-muted-foreground',
            feed ? 'text-xs leading-[18px]' : 'truncate text-xs',
          )}
          title={body}
        >
          {withEmailLink(body, emailAccountId)}
        </p>
      )}
    </div>
  );
}
